import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ClockIcon from "src/assets/svg-icons/clock.svg";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  useGetKAFDBooking,
  useUpdateKafdBookingMutation,
} from "src/api/kafd-prebooking";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import downArrowIcon from "src/assets/svg-icons/down-arrow.svg";
import sarIcon from "src/assets/svg-icons/sar.svg";
import { calculateLocationPrice, generateTimeOptions } from "../../constants";
import CheckedOutTicket from "src/pages/car-request/components/checked-out-ticket";
import FailedTicket from "src/pages/car-request/components/failed-ticket";
import moment from "moment";

const policyPoints = ["policyPoint1", "policyPoint2", "policyPoint3"];

interface IProps {}

export const schema = Yup.object().shape({
  stayingTime: Yup.number()
    .typeError("Required")
    .test("is-required", "Required", (value) => !!value), // manual required,
});

export interface IFormInputs extends Yup.InferType<typeof schema> {}

const ExtendForm: React.FC<IProps> = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [price, setPrice] = useState(0);
  const [extended, setExtended] = useState(false);

  //   APIS
  const {
    data: bookingData,
    isFetching,
    error,
  } = useGetKAFDBooking(params.id as string);
  const { mutate, status, isPending, data } = useUpdateKafdBookingMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IFormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { hasGlobalDiscount, globalDiscountPer, isNoShow, ticketStatus } =
    bookingData?.data || {};

  const HOUR_MS = 60 * 60 * 1000;

  const newLeavingTime =
    bookingData?.data.leavingTime + Number(watch("stayingTime")) * HOUR_MS;

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    mutate({
      id: params.id as string,
      newLeavingTime: newLeavingTime,
    });
  };

  const afterDiscountPercentageRatio = hasGlobalDiscount
    ? (100 - globalDiscountPer) / 100
    : 1;

  const priceToDisplay = !watch("stayingTime")
    ? 0
    : Math.max(price - bookingData?.data?.amount, 0);

  console.log({ price, priceToDisplay });

  const leaveTimeDate = new Date(bookingData?.data?.leavingTime);

  const options = generateTimeOptions(
    {
      hour: leaveTimeDate.getHours(),
      minute: leaveTimeDate.getMinutes(),
    },
    {
      hour: bookingData?.data?.workingTo?.hour,
      minute: bookingData?.data?.workingTo?.minute,
    }
  );

  useEffect(() => {
    if (watch("stayingTime")) {
      const price = calculateLocationPrice({
        startTime: moment(bookingData?.data?.arrivingTime).toDate(),
        endTime: moment(newLeavingTime).toDate(),
        service: bookingData?.data?.service,
      });
      console.log({ price, bookingData });
      setPrice(price * afterDiscountPercentageRatio);
    }
  }, [watch("stayingTime")]);

  useEffect(() => {
    if (status === "success") {
      if (data?.data.paymentUrl) {
        window.location.href = data?.data.paymentUrl;
      } else {
        setNotifications([
          ...notifications,
          {
            type: "success",
            message: t("bookedSuccessfully"),
          },
        ]);
        window.location.reload();
      }
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  return (
    <Box borderRadius={"12px"} overflow={"hidden"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          bgcolor={"#272727"}
          padding={{ xs: "30px 20px 100px 20px", md: "30px 113px 100px 113px" }}
          alignItems={"center"}
        >
          {isFetching ? (
            <Box height={"500px"} display={"flex"} alignItems={"center"}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error || isNoShow ? (
                <FailedTicket
                  title={isNoShow ? t("linkExpiredMessage") : ""}
                  description={isNoShow ? " " : ""}
                />
              ) : (
                <>
                  {ticketStatus === "closed" ? (
                    <CheckedOutTicket />
                  ) : (
                    <>
                      <img src={ClockIcon} height={200} alt="" />

                      <Typography
                        mb={2}
                        color={"primary"}
                        fontSize={"24px"}
                        fontWeight={700}
                        textTransform={"uppercase"}
                        align="center"
                      >
                        {t("updateGuestLeavingTime")}!
                      </Typography>
                      <Typography mb={2} align="center">
                        {t("extraChargesApplied")}
                      </Typography>
                      <Box maxWidth={"629px"} width={"100%"}>
                        <Box width={"100%"}>
                          <Typography sx={{ mb: "10px" }}>
                            {t("extendFor")}
                          </Typography>
                          <FormControl
                            fullWidth
                            error={!!errors.stayingTime}
                            size="medium"
                          >
                            <Controller
                              name="stayingTime"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  displayEmpty
                                  labelId="stayingTime-select-label"
                                  id="stayingTime-simple-select"
                                  sx={{ bgcolor: "#222" }}
                                >
                                  <MenuItem value={undefined} disabled>
                                    {t("extendFor")}
                                  </MenuItem>
                                  {options?.map((option) => {
                                    return (
                                      <MenuItem key={option} value={option}>
                                        {option}{" "}
                                        {t(option > 1 ? "hours" : "hour")}
                                      </MenuItem>
                                    );
                                  })}
                                  {options.length === 0 && (
                                    <MenuItem disabled>
                                      {t("reachedMaxHoursOfBooking")}
                                    </MenuItem>
                                  )}
                                </Select>
                              )}
                            />
                            <FormHelperText id="my-helper-text">
                              {t(`${errors.stayingTime?.message ?? ""}`)}
                            </FormHelperText>
                          </FormControl>
                        </Box>
                        {/* price */}
                        <Box width={"100%"} mt={4}>
                          <Typography mb={"10px"}>
                            {t("valetServiceNewFees")}
                          </Typography>
                          <Box
                            display={"flex"}
                            flexDirection={"row"}
                            gap={"4px"}
                            bgcolor={"#38383880"}
                            p={"20px"}
                            borderRadius={"12px"}
                          >
                            <Typography>{t("price")}: </Typography>
                            <Stack
                              direction={
                                i18n.language === "en" ? "row" : "row-reverse"
                              }
                              alignItems={"center"}
                              gap={"4px"}
                            >
                              <img height={13} src={sarIcon} alt="" />
                              <Typography fontSize={"17px"}>
                                {priceToDisplay}
                              </Typography>
                            </Stack>
                            {/* {hasGlobalDiscount && priceToDisplay > 0 && (
                              <Typography color={"#4EBE86"}>
                                ( %{globalDiscountPer} {t("discountApplied")} )
                              </Typography>
                            )} */}
                          </Box>
                          <Typography color={"primary"} mt={"10px"}>
                            {t("PaidFeeNotRefundableAndExtraFeesMightApplied")}
                          </Typography>
                        </Box>

                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          mt={3}
                          mb={2}
                          gap={3}
                          sx={{ cursor: "pointer" }}
                          onClick={() => setExtended((prev) => !prev)}
                        >
                          <Typography color={"primary"} fontWeight={500}>
                            {t("usagePolicy")}
                          </Typography>
                          <Box
                            sx={{
                              transform: extended ? "scaleY(-1)" : "unset",
                            }}
                          >
                            <img height={8} src={downArrowIcon} alt="" />
                          </Box>
                        </Stack>

                        {extended &&
                          policyPoints.map((point) => (
                            <Stack
                              key={point}
                              direction={"row"}
                              gap={1.2}
                              mb={0.4}
                            >
                              <Box
                                minWidth={"5px"}
                                height={"5px"}
                                bgcolor={"#D0D0D0"}
                                mt={"10px"}
                              />
                              <Typography color={"#D0D0D0"} lineHeight={"26px"}>
                                {t(point)}
                              </Typography>
                            </Stack>
                          ))}
                      </Box>

                      <LoadingButton
                        sx={{ minWidth: { xs: "100%", sm: "310px" }, mt: 6 }}
                        variant="contained"
                        size="large"
                        loading={isPending}
                        type="submit"
                      >
                        {t("updateBooking")}
                      </LoadingButton>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </form>
    </Box>
  );
};

export default ExtendForm;
